import Dialog from "../../General/ConfirmableDialog";
import { useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import { formatDate } from "../../../helpers/formatingFunctions";
import OwnerCompanyInfoModal from "../OwnerCompanyInfoModal/OwnerCompanyInfoModal";
import RentDetailsPopup from "../RentDetailsPopup/RentDetailsPopup";

const clickableCellStyle = {
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
};

const fetchCompanyHistory = async (
  token: string | null | undefined,
  assetId: string,
  assetType: string,
) => {
  const { data } = await axios.get(
    `${apiVariable}/ownerCompany/${assetId}/${assetType}CompanyHistory`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return data;
};

export const updateCompanyHistoryNote = async (
  token: string | null | undefined,
  companyHistory: any,
) => {
  const { data } = await axios.put(
    `${apiVariable}/updateCompanyHistoryNote`,
    companyHistory,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};

interface RentalHistoryProps {
  assetId: string;
  token: string | null | undefined;
  assetType: "truck" | "trailer";
}

const RentalHistory = ({ assetId, token, assetType }: RentalHistoryProps) => {
  const [openNotesModal, setOpenNotesModal] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<any>(null);
  const [billToModalOpen, setBillToModalOpen] = useState(false);
  const [selectedBillToId, setSelectedBillToId] = useState<string | null>(null);
  const [rentDetailsOpen, setRentDetailsOpen] = useState(false);
  const [selectedRentDetails, setSelectedRentDetails] = useState<any>(null);
  const queryClient = useQueryClient();

  const {
    data: companyHistory,
    isLoading: isLoadingCompanyHistory,
    isError: isErrorCompanyHistory,
    error: companyHistoryError,
  } = useQuery({
    queryKey: ["companyHistory", assetId],
    queryFn: () => fetchCompanyHistory(token, assetId, assetType),
    enabled: !!assetId,
  });

  const handleOpenNotesModal = (row: any) => {
    setSelectedHistoryItem(row);
    setNewNote(row.note || "");
    setOpenNotesModal(true);
  };

  const handleCloseNotesModal = () => {
    setOpenNotesModal(false);
    setSelectedHistoryItem(null);
    setNewNote("");
  };

  const handleAddNote = async () => {
    if (!selectedHistoryItem) return;

    try {
      await updateCompanyHistoryNote(token, {
        ...selectedHistoryItem,
        note: newNote,
      });

      handleCloseNotesModal();
      queryClient.invalidateQueries({ queryKey: ["companyHistory"] });
    } catch (error) {
      console.error(error);
    }
  };

  const handleRowClick = (params: any) => {
    setSelectedRentDetails({
      companyId: params.row.companyId,
      startDate: params.row.dateStarted,
      endDate: params.row.dateEnded,
    });
    setRentDetailsOpen(true);
  };

  if (isLoadingCompanyHistory) {
    return <CircularProgress />;
  }

  if (isErrorCompanyHistory) {
    return (
      <Typography color="error">
        Error: {(companyHistoryError as Error).message}
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ height: 400, width: { xs: "100%", sm: "80%" }, minWidth: 0 }}>
        <DataGrid
          rows={companyHistory || []}
          columns={[
            {
              field: "name",
              headerName: "Company Name",
              width: 300,
              renderCell: (params: any) => {
                const { row } = params;
                return (
                  <Typography
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBillToId(row.companyId);
                      setBillToModalOpen(true);
                    }}
                    sx={{
                      cursor: "pointer",
                      textDecoration: "none",
                      textAlign: "left",
                      display: "flex",
                      justifyContent: "left",
                      alignItems: "center",
                      height: "100%",
                      width: "100%",
                    }}
                  >
                    {row.name}
                  </Typography>
                );
              },
            },
            {
              field: "transactionType",
              headerName: "Transaction Type",
              width: 300,
              renderCell: (params) => (
                <Box sx={clickableCellStyle}>{params.value}</Box>
              ),
            },
            {
              field: "dateStarted",
              headerName: "Date Started",
              width: 300,
              valueFormatter: (params: any) =>
                params ? formatDate(params) : "",
              renderCell: (params) => (
                <Box sx={clickableCellStyle}>{params.formattedValue}</Box>
              ),
            },
            {
              field: "dateEnded",
              headerName: "Date Ended",
              width: 300,
              valueFormatter: (params: any) =>
                params ? formatDate(params) : "",
              renderCell: (params) => (
                <Box sx={clickableCellStyle}>{params.formattedValue}</Box>
              ),
            },
            {
              field: "note",
              headerName: "Note",
              width: 300,
              renderCell: (params) => (
                <Box sx={clickableCellStyle}>{params.value}</Box>
              ),
            },
            {
              field: "addedBy",
              headerName: "Modified by",
              width: 200,
              renderCell: (params) => (
                <Box sx={clickableCellStyle}>{params.value}</Box>
              ),
            },
            {
              field: "lastModifiedDate",
              headerName: "Last Modified Date",
              width: 300,
              valueFormatter: (params: any) =>
                params ? formatDate(params) : "",
              renderCell: (params) => (
                <Box sx={clickableCellStyle}>{params.formattedValue}</Box>
              ),
            },
            {
              field: "actions",
              headerName: "",
              width: 150,
              renderCell: (params: any) => {
                const { row } = params;
                return (
                  <Button
                    variant="contained"
                    onClick={() => handleOpenNotesModal(row)}
                  >
                    Add Note
                  </Button>
                );
              },
            },
          ]}
          onRowClick={handleRowClick}
          sx={{
            "& .MuiDataGrid-row": {
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            },
          }}
        />
      </Box>

      <Dialog open={openNotesModal} onClose={handleCloseNotesModal}>
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Note"
            multiline
            rows={4}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotesModal}>Cancel</Button>
          <Button onClick={handleAddNote}>Add Note</Button>
        </DialogActions>
      </Dialog>

      <OwnerCompanyInfoModal
        id={selectedBillToId || ""}
        open={billToModalOpen}
        onClose={() => setBillToModalOpen(false)}
      />

      {selectedRentDetails && (
        <RentDetailsPopup
          open={rentDetailsOpen}
          onClose={() => setRentDetailsOpen(false)}
          companyId={selectedRentDetails.companyId}
          assetId={assetId}
          assetType={assetType}
          startDate={selectedRentDetails.startDate}
          endDate={selectedRentDetails.endDate}
          token={token}
        />
      )}
    </>
  );
};

export default RentalHistory;
